/**
 * MODULE: domain/pages/frontend/utils/pageDatabaseEmbedUtils.ts
 * PURPOSE: Helpers for inline database embed blocks in note pages
 */

import type { DatabaseRow, FieldDefinition } from '../../contracts/pages.types';
import { filterDatabaseRows } from './databaseBoardModel';

export type DatabaseEmbedBlockContent = {
  database_page_id: string;
  workspace_id: string;
  database_title: string;
  database_icon: string | null;
  max_rows: number;
  search_filter: string;
};

export const DATABASE_EMBED_ROW_LIMITS = [5, 10, 20] as const;
export const DEFAULT_DATABASE_EMBED_MAX_ROWS = 10;

export function clampDatabaseEmbedMaxRows(value: number): number {
  if (Number.isNaN(value)) return DEFAULT_DATABASE_EMBED_MAX_ROWS;
  return Math.min(50, Math.max(1, Math.round(value)));
}

export function buildDatabaseEmbedContent(input: {
  databasePageId: string;
  workspaceId: string;
  databaseTitle: string;
  databaseIcon?: string | null;
  maxRows?: number;
  searchFilter?: string;
}): DatabaseEmbedBlockContent | null {
  if (!input.databasePageId || !input.workspaceId) return null;
  return {
    database_page_id: input.databasePageId,
    workspace_id: input.workspaceId,
    database_title: input.databaseTitle.trim() || 'Untitled database',
    database_icon: input.databaseIcon ?? null,
    max_rows: clampDatabaseEmbedMaxRows(input.maxRows ?? DEFAULT_DATABASE_EMBED_MAX_ROWS),
    search_filter: input.searchFilter?.trim() ?? '',
  };
}

export function readDatabaseEmbedFromAttrs(attrs: Record<string, unknown>): DatabaseEmbedBlockContent | null {
  const databasePageId = typeof attrs.databasePageId === 'string' ? attrs.databasePageId : '';
  const workspaceId = typeof attrs.workspaceId === 'string' ? attrs.workspaceId : '';
  if (!databasePageId || !workspaceId) return null;

  return {
    database_page_id: databasePageId,
    workspace_id: workspaceId,
    database_title:
      typeof attrs.databaseTitle === 'string' ? attrs.databaseTitle : 'Untitled database',
    database_icon: typeof attrs.databaseIcon === 'string' ? attrs.databaseIcon : null,
    max_rows: clampDatabaseEmbedMaxRows(Number(attrs.maxRows ?? DEFAULT_DATABASE_EMBED_MAX_ROWS)),
    search_filter: typeof attrs.searchFilter === 'string' ? attrs.searchFilter : '',
  };
}

export function databaseEmbedContentToAttrs(
  content: DatabaseEmbedBlockContent,
  host?: { pageId: string }
): Record<string, unknown> {
  return {
    databasePageId: content.database_page_id,
    workspaceId: content.workspace_id,
    databaseTitle: content.database_title,
    databaseIcon: content.database_icon,
    maxRows: content.max_rows,
    searchFilter: content.search_filter,
    hostPageId: host?.pageId ?? null,
  };
}

export function applyDatabaseEmbedDisplay(
  rows: DatabaseRow[],
  fields: FieldDefinition[],
  searchFilter: string,
  maxRows: number
): { rows: DatabaseRow[]; visibleCount: number; totalMatching: number; truncated: boolean } {
  const filtered = filterDatabaseRows(rows, fields, searchFilter);
  const limit = clampDatabaseEmbedMaxRows(maxRows);
  const visible = filtered.slice(0, limit);
  return {
    rows: visible,
    visibleCount: visible.length,
    totalMatching: filtered.length,
    truncated: filtered.length > visible.length,
  };
}

export function databaseEmbedSummaryLabel(
  visibleCount: number,
  totalMatching: number,
  truncated: boolean
): string {
  if (totalMatching === 0) return 'No matching rows';
  if (truncated) {
    return `Showing ${visibleCount} of ${totalMatching} matching rows`;
  }
  return `${totalMatching} row${totalMatching === 1 ? '' : 's'}`;
}
